import React, {
  type ReactElement,
  useEffect,
  useMemo,
  useRef,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  AccessibilityInfo,
  Animated,
  PanResponder,
  Pressable,
  StyleSheet,
  type TextStyle,
  View,
} from "react-native";

import type { IconProps } from "../icons/Icon";
import { Text } from "../Text";
import { useTheme } from "../ThemeProvider";
import { useAnimatedNative } from "../util/useAnimatedNative";

export type ToastVariant = "feedback" | "notification";

export interface ToastProps {
  /**
   * The text to display in the toast
   */
  text?: string;
  /**
   * Additional explanatory text to display under the main text
   */
  explainerText?: string;
  /**
   * The icon to display in the toast
   */
  icon?: ReactElement;
  /**
   * Accessibility label for the icon. If not provided, the icon is treated as decorative.
   */
  iconAccessibilityLabel?: string;
  /**
   * The style and animation direction of the toast
   * @default "feedback"
   */
  variant?: ToastVariant;
  /**
   * The text to display on the button. If not provided, the button will not be displayed.
   */
  buttonLabel?: string;
  /**
   * Callback when the button is pressed. If not provided, the button will not be displayed.
   */
  buttonAction?: () => void;
  /**
   * Test ID for the button element
   */
  buttonTestId?: string;
  /**
   * Duration in milliseconds before the toast automatically dismisses
   * @default 4000
   */
  timeout?: number;
  /**
   * Callback when the toast is dismissed and has animated off screen.
   */
  onDismiss: () => void;
  /**
   * Custom vertical offset from the top/bottom of the screen
   * @default 16
   */
  verticalOffset?: number;
}

/**
 * A handle to control the toast's animation
 */
export interface ToastHandle {
  /**
   * Animates the toast off screen and calls the provided callback when complete
   * @param onComplete - Callback function to be called after the animation finishes
   */
  animateOut: (onComplete: () => void) => void;
}

/**
 * A toast component that animates in from the bottom or top of the screen
 */
export const Toast = forwardRef<ToastHandle, ToastProps>(
  (
    {
      icon,
      iconAccessibilityLabel,
      text,
      explainerText,
      variant = "feedback",
      buttonAction,
      buttonLabel,
      buttonTestId,
      timeout = 4000,
      onDismiss,
      verticalOffset,
    },
    ref,
  ): ReactElement => {
    const theme = useTheme();
    const defaultVerticalOffset = verticalOffset ?? theme.spacing.s4;
    const { translateY } = useMemo(
      () => ({
        translateY: new Animated.Value(
          variant === "feedback" ? 100 + defaultVerticalOffset : -(100 + defaultVerticalOffset),
        ),
      }),
      [variant, defaultVerticalOffset],
    );
    const styles = useMemo(
      () =>
        StyleSheet.create({
          container: {
            position: "absolute",
            left: theme.spacing.s4,
            right: theme.spacing.s4,
            backgroundColor: theme.colors.backgroundInverse,
            borderRadius: theme.borderRadius.md,
            padding: theme.spacing.s4,
            flexDirection: "row",
            alignItems: "center",
          },
          feedback: {
            bottom: theme.spacing.s4,
          },
          notification: {
            top: theme.spacing.s4,
          },
          contentContainer: {
            flex: 1,
            flexDirection: "row",
            alignItems: "center",
          },
          iconContainer: {
            marginRight: theme.spacing.s2,
          },
          labelContainer: {
            flex: 1,
          },
          buttonContainer: {
            marginLeft: theme.spacing.s2,
          },
          text: {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
            fontWeight: String(theme.fontWeights.bold) as TextStyle["fontWeight"],
            color: theme.colors.foregroundInverse,
          },
          explainerText: {
            color: theme.colors.foregroundInverse,
          },
        }),
      [theme],
    );

    const animationDuration = 400;
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    // Helper to build accessibility announcement text
    const buildAccessibilityText = useCallback(() => {
      return [iconAccessibilityLabel, text, explainerText, buttonLabel].filter(Boolean).join(". ");
    }, [iconAccessibilityLabel, text, explainerText, buttonLabel]);

    const useNativeDriver = useAnimatedNative();

    const dismissAnimation = useCallback(
      (onComplete?: () => void) => {
        // Clear the timeout to comply with WCAG timing requirements
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = undefined;
        }

        const animation = Animated.timing(translateY, {
          toValue:
            variant === "feedback" ? 100 + defaultVerticalOffset : -(100 + defaultVerticalOffset),
          duration: animationDuration,
          useNativeDriver: useNativeDriver,
        });

        animation.start(() => {
          // notify that the toast has been dismissed
          onDismiss();
          // call onComplete for the ToastHandle animateOut function
          onComplete?.();
        });

        // Note: This animation is part of dismissal flow, so we don't need cleanup here
        // as the component will unmount after dismissal
      },
      [translateY, variant, defaultVerticalOffset, animationDuration, onDismiss, useNativeDriver],
    );

    useImperativeHandle(ref, () => ({
      animateOut: dismissAnimation,
    }));

    const panResponder = useRef(
      PanResponder.create({
        onMoveShouldSetPanResponder: (_, gestureState) => {
          const isVerticalSwipe = Math.abs(gestureState.dy) > Math.abs(gestureState.dx);
          const isSwipeInCorrectDirection =
            (variant === "feedback" && gestureState.dy > 0) ||
            (variant === "notification" && gestureState.dy < 0);
          return isVerticalSwipe && isSwipeInCorrectDirection;
        },
        onPanResponderMove: (_, gestureState) => {
          if (
            (gestureState.dy > 0 && variant === "feedback") ||
            (gestureState.dy < 0 && variant === "notification")
          ) {
            translateY.setValue(gestureState.dy);
          }
        },
        onPanResponderRelease: (_, gestureState) => {
          const shouldDismiss = Math.abs(gestureState.dy) > 50 || Math.abs(gestureState.vy) > 0.5;
          if (shouldDismiss) {
            dismissAnimation();
          } else {
            const springAnimation = Animated.spring(translateY, {
              toValue: 0,
              useNativeDriver: useNativeDriver,
            });
            springAnimation.start();
            // Note: This is a quick spring back animation, cleanup not critical
            // as it's very short-lived and component remains mounted
          }
        },
      }),
    ).current;

    useEffect(() => {
      const animation = Animated.timing(translateY, {
        toValue: 0,
        duration: animationDuration,
        useNativeDriver: useNativeDriver,
      });

      animation.start(() => {
        // Announce the toast content for screen readers after animation completes
        const announcement = buildAccessibilityText();
        if (announcement) {
          AccessibilityInfo.announceForAccessibility(announcement);
        }
      });

      timeoutRef.current = setTimeout(() => {
        dismissAnimation();
      }, timeout);

      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = undefined;
        }
        animation.stop();
      };
    }, [
      timeout,
      translateY,
      animationDuration,
      dismissAnimation,
      buildAccessibilityText,
      useNativeDriver,
    ]);

    const styledIcon = icon
      ? // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
        React.cloneElement(icon as React.ReactElement<IconProps>, {
          color: theme.colors.foregroundInverse,
          testID: "toast-icon",
          // If no accessibility label is provided, treat icon as decorative
          "aria-hidden": !iconAccessibilityLabel,
        })
      : null;

    return (
      <Animated.View
        {...panResponder.panHandlers}
        testID="toast-container"
        accessibilityRole="alert"
        accessibilityLabel={buildAccessibilityText()}
        style={[
          styles.container,
          {
            [variant === "feedback" ? "bottom" : "top"]: defaultVerticalOffset,
          },
          {
            transform: [{ translateY }],
          },
        ]}
      >
        <View style={styles.contentContainer}>
          {icon && <View style={styles.iconContainer}>{styledIcon}</View>}
          <View style={styles.labelContainer} accessible={true}>
            {text && (
              <Text level={3} sx={styles.text}>
                {text}
              </Text>
            )}
            {explainerText && (
              <Text level={2} sx={styles.explainerText}>
                {explainerText}
              </Text>
            )}
          </View>
          {buttonAction && buttonLabel && (
            <View style={styles.buttonContainer}>
              <Pressable
                onPress={() => {
                  // Clear timeout when button is pressed
                  if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                    timeoutRef.current = undefined;
                  }
                  buttonAction();
                }}
                aria-label={buttonLabel}
                accessibilityRole="button"
                testID={buttonTestId}
              >
                <Text
                  level={3}
                  sx={{
                    color: theme.colors.foregroundInverse,
                    fontWeight: "bold",
                  }}
                >
                  {buttonLabel}
                </Text>
              </Pressable>
            </View>
          )}
        </View>
      </Animated.View>
    );
  },
);
